import { createInvoiceThunk, InvoicesStatus } from '@spike/invoices-action';
import { CheckoutEnrollment, CheckoutOrderItemEnrollments } from '@spike/membership-model';
import { fetchCheckoutEnrollmentsThunk, MembershipsStatus } from '@spike/memberships-action';
import { showWarning } from '@spike/notifications-action';
import { Order, RedeemTypes } from '@spike/order-model';
import { OrdersStatus, reloadOrderThunk, saveOrderRedeemsThunk } from '@spike/orders-action';
import useNonInitialEffect from '@versiondos/hooks';
import clsx from 'clsx';
import { Button } from 'components/UI';
import { useApiClientWrapper } from 'hooks';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { groupOrderItemsByPet, sortPets } from '../utils/OrderUtils';
import styles from './Memberships.module.css';
import PetMemberships from './PetMemebrships';

export interface MembershipsProps {
    order: Order;
    bookingId?: number;
    className?: string;
    onClose: () => void;
}

export const Memberships: FunctionComponent<MembershipsProps> = props => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const ordersStatus = useSelector<RootState, OrdersStatus>(state => state.orders.status);
    const invoicesStatus = useSelector<RootState, InvoicesStatus>(state => state.invoices.status);

    const membershipsStatus = useSelector<RootState, MembershipsStatus>(state => state.memberships.status);
    const checkoutOrderItemsEnrollments = useSelector<RootState, Array<CheckoutOrderItemEnrollments>>(
        state => state.memberships.checkoutEnrollments
    );

    const sortedPets = useMemo(() => sortPets(props.order), [props.order]);

    const orderItemsByPet = useMemo(() => groupOrderItemsByPet(props.order), [props.order]);

    const enrollmentsByOrderItem = useMemo(
        () =>
            checkoutOrderItemsEnrollments.reduce((map, checkoutOrderItem) => {
                map.set(checkoutOrderItem.orderItemId, [...checkoutOrderItem.enrollments]);
                return map;
            }, new Map<number, Array<CheckoutEnrollment>>()),
        [checkoutOrderItemsEnrollments]
    );

    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState({ order: false, invoice: false, enrollments: true });
    const [changed, setChanged] = useState(false);

    const loadEnrollmentsAvailableForOrder = () =>
        dispatch(fetchCheckoutEnrollmentsThunk(apiClientWrapper, props.order.id));

    const reloadAll = () => {
        dispatch(reloadOrderThunk(apiClientWrapper));
        dispatch(createInvoiceThunk(apiClientWrapper, props.order.id, props.bookingId));
        loadEnrollmentsAvailableForOrder();
        setSaving(false);
        setLoading(prev => ({ ...prev, order: true, invoice: true, enrollments: true }));
    };

    useEffect(() => {
        loadEnrollmentsAvailableForOrder();
    }, []);

    useNonInitialEffect(() => {
        switch (membershipsStatus) {
            case MembershipsStatus.FetchingCheckoutEnrollmentsSuccess:
            case MembershipsStatus.FetchingCHeckoutEnrollmentsError:
                setLoading(prev => ({ ...prev, enrollments: false }));
        }
    }, [membershipsStatus]);

    useNonInitialEffect(() => {
        switch (ordersStatus) {
            case OrdersStatus.SaveOrderRedeemSuccess:
                reloadAll();
                break;
            case OrdersStatus.Unprocessable:
                dispatch(showWarning('The quota run out for this membership.'));
                reloadAll();
                break;
            case OrdersStatus.GetSuccess:
                setLoading(prev => ({ ...prev, order: false }));
                break;
            case OrdersStatus.ReloadSuccess:
                setLoading(prev => ({ ...prev, order: false }));
                break;
            case OrdersStatus.Error:
                setSaving(false);
        }
    }, [ordersStatus]);

    useNonInitialEffect(() => {
        switch (invoicesStatus) {
            case InvoicesStatus.CreateInvoiceSuccess:
            case InvoicesStatus.Error:
                setLoading(prev => ({ ...prev, invoice: false }));
        }
    }, [invoicesStatus]);

    const saveHandler = (orderItemId: number, enrollmentId: number) => {
        setSaving(true);
        setChanged(true);

        const redeem = {
            orderItemId,
            redeem: { type: RedeemTypes.ENROLLMENT, id: enrollmentId }
        };

        dispatch(saveOrderRedeemsThunk(apiClientWrapper, props.order.id, [redeem]));
    };

    const deleteHandler = (orderItemId: number) => {
        setSaving(true);
        setChanged(true);

        const redeem = {
            orderItemId,
            redeem: undefined
        };

        dispatch(saveOrderRedeemsThunk(apiClientWrapper, props.order.id, [redeem]));
    };

    return (
        <div className={styles.container}>
            <OverlayScrollbarsComponent style={{ height: '100%' }}>
                <div className={clsx(styles.body, styles.horizontalPaddings)}>
                    {sortedPets.map(
                        pet =>
                            pet && (
                                <PetMemberships
                                    key={pet.id}
                                    pet={pet}
                                    className={styles.section}
                                    petOrderItems={
                                        orderItemsByPet.get(pet.id ?? 0) ?? {
                                            appointments: [],
                                            products: [],
                                            addOnServices: [],
                                            customs: [],
                                            memberships: []
                                        }
                                    }
                                    enrollmentsByOrderItem={enrollmentsByOrderItem}
                                    saving={saving}
                                    loading={loading.order || loading.invoice || loading.enrollments}
                                    onSave={saveHandler}
                                    onDelete={deleteHandler}
                                />
                            )
                    )}
                    <div className={styles.bodyBottomSpace} />
                </div>
            </OverlayScrollbarsComponent>
            <div className={clsx(styles.footer, styles.horizontalPaddings)}>
                <Button
                    color="green"
                    label="Close"
                    className={styles.button}
                    disabled={loading.order || loading.invoice || loading.enrollments || saving || !changed}
                    onClick={props.onClose}
                />
            </div>
        </div>
    );
};

export default Memberships;
